<template>
  <div class="app-checkbox-group" :class="rootClasses">
    <div v-if="label" class="app-checkbox-group__label">
      {{ label }}
      <span v-if="showRequiredMark" class="app-checkbox-group__required" aria-hidden="true">*</span>
    </div>

    <div class="app-checkbox-group__items" :style="layoutStyle">
      <label
        v-for="(opt, idx) in options"
        :key="getValue(opt)"
        class="app-checkbox"
        :class="{ 'app-checkbox--disabled': isDisabled(opt) }"
      >
        <input
          type="checkbox"
          :name="name"
          :value="getValue(opt)"
          :checked="isChecked(opt)"
          :disabled="isDisabled(opt)"
          class="app-checkbox__input"
          @change="onToggle(opt, idx, $event)"
        />
        <span class="app-checkbox__box">
          <i class="fa-solid fa-check app-checkbox__icon"></i>
        </span>
        <span class="app-checkbox__label">
          <slot name="option" :option="opt" :index="idx">
            {{ getLabel(opt) }}
          </slot>
        </span>
      </label>
    </div>

    <small
      v-if="error"
      class="app-checkbox-group__error"
      v-html="error"
    ></small>
    <small
      v-else-if="help"
      class="app-checkbox-group__help"
      v-html="help"
    ></small>
  </div>
</template>

<script>
/**
 * App-styled checkbox group — no-Bootstrap, modern v-model contract.
 *
 * Unlike `c-checkbox-group` (which mutates `options[].checked` in place),
 * this group keeps `options` static and emits an ARRAY of selected values.
 * Standard Vue convention, predictable, easy to v-model.
 *
 * Props (mirroring c-checkbox-group where it makes sense):
 *   value         — Array of currently-selected values (v-model)
 *   options       — Array of items (any shape)
 *   label         — group heading
 *   help / error  — inline messages
 *   valueKey      — option key for value (default 'value')
 *   labelKey      — option key for label (default 'label', fallback 'text')
 *   disabledKey   — option key for disabled flag (default 'disabled')
 *   cols          — grid columns count (overrides direction)
 *   gap           — CSS gap value (default '8px 16px')
 *   direction     — 'row' | 'column' (default 'row')
 *   name          — input name (shared across all checkboxes)
 *   disabled      — disable all
 *   required      — marks group label with *
 *   size          — 'sm' | 'md' (default 'md')
 *
 * Slots:
 *   option — scoped slot ({ option, index }) for custom per-option label.
 */
export default {
  name: 'AppCheckboxGroup',

  props: {
    value:        { type: Array,   default: () => [] },
    options:      { type: Array,   required: true },
    label:        { type: String,  default: '' },
    help:         { type: String,  default: '' },
    error:        { type: String,  default: '' },
    valueKey:     { type: String,  default: 'value' },
    labelKey:     { type: String,  default: 'label' },
    disabledKey:  { type: String,  default: 'disabled' },
    cols:         { type: Number,  default: 0 },
    gap:          { type: String,  default: '8px 16px' },
    direction: {
      type: String,
      default: 'row',
      validator: (v) => ['row', 'column'].includes(v),
    },
    name:         { type: String,  default: '' },
    disabled:     { type: Boolean, default: false },
    required:     { type: Boolean, default: false },
    requiredMark: { type: Boolean, default: false },
    size: {
      type: String,
      default: 'md',
      validator: (v) => ['sm', 'md'].includes(v),
    },
  },

  emits: ['input', 'change'],

  computed: {
    showRequiredMark() { return this.requiredMark || this.required },

    layoutStyle() {
      if (this.cols > 0) {
        return {
          display: 'grid',
          gridTemplateColumns: `repeat(${this.cols}, auto)`,
          gap: this.gap,
          justifyContent: 'flex-start',
        }
      }
      return {
        display: 'flex',
        flexDirection: this.direction === 'column' ? 'column' : 'row',
        flexWrap: 'wrap',
        gap: this.gap,
      }
    },

    rootClasses() {
      return [
        `app-checkbox-group--${this.size}`,
        {
          'app-checkbox-group--has-error': !!this.error,
          'app-checkbox-group--disabled': this.disabled,
        },
      ]
    },
  },

  methods: {
    getValue(opt)   { return opt[this.valueKey] ?? opt.value },
    getLabel(opt)   { return opt[this.labelKey] ?? opt.label ?? opt.text ?? '' },
    isDisabled(opt) { return this.disabled || !!opt[this.disabledKey] },

    isChecked(opt) {
      const v = this.getValue(opt)
      return Array.isArray(this.value) && this.value.includes(v)
    },

    onToggle(opt, idx, event) {
      const v = this.getValue(opt)
      const set = new Set(Array.isArray(this.value) ? this.value : [])
      if (set.has(v)) set.delete(v)
      else set.add(v)
      const next = [...set]
      this.$emit('input', next)
      this.$emit('change', next, opt, event)
    },
  },
}
</script>

<style lang="scss" scoped>
$acg-text:       #2b3a4a;
$acg-text-muted: #6c7a89;
$acg-bg-surface: #ffffff;
$acg-bg-soft:    #f6f8fa;
$acg-border:     #cfd6dc;
$acg-blue:       #1f73c1;
$acg-blue-focus: #4392d4;
$acg-red:        #d04848;
$acg-radius:     4px;
$acg-duration:   140ms;
$acg-ease:       cubic-bezier(.4, 0, .2, 1);

.app-checkbox-group {
  &__label {
    font-size: 11px;
    font-weight: 700;
    color: $acg-text-muted;
    text-transform: uppercase;
    letter-spacing: .04em;
    margin-bottom: 8px;
    line-height: 1.2;
  }

  &__required {
    color: $acg-red;
    margin-left: 2px;
  }

  &__items { min-width: 0; }

  &__error,
  &__help {
    display: block;
    font-size: 11px;
    margin-top: 6px;
    line-height: 1.4;
  }
  &__error { color: $acg-red; }
  &__help  { color: $acg-text-muted; }

  &--disabled { opacity: 0.7; pointer-events: none; }
}

// ── Checkbox item styling (mirrors app-checkbox for visual consistency) ──
.app-checkbox {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;

  &--disabled { cursor: not-allowed; opacity: 0.7; }

  &__input {
    position: absolute;
    opacity: 0;
    pointer-events: none;
    width: 1px; height: 1px; margin: -1px;
  }

  &__box {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    border-radius: $acg-radius;
    background: $acg-bg-surface;
    border: 1px solid $acg-border;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    transition: background $acg-duration $acg-ease, border-color $acg-duration $acg-ease, box-shadow $acg-duration $acg-ease;
  }

  &__icon {
    font-size: 9px;
    line-height: 1;
    opacity: 0;
    transform: scale(0.6);
    transition: opacity $acg-duration $acg-ease, transform $acg-duration $acg-ease;
  }

  &__label {
    font-size: 13px;
    color: $acg-text;
    line-height: 1.4;
  }

  &:hover &__box { border-color: $acg-blue-focus; }

  &__input:checked + &__box {
    background: $acg-blue;
    border-color: $acg-blue;

    .app-checkbox__icon { opacity: 1; transform: scale(1); }
  }

  &__input:focus-visible + &__box {
    box-shadow: 0 0 0 3px rgba(67, 146, 212, 0.20);
  }

  &__input:disabled + &__box {
    background: $acg-bg-soft;
    border-color: rgba(0, 0, 0, 0.10);
  }
}

// Size variant
.app-checkbox-group--sm {
  .app-checkbox__box   { width: 14px; height: 14px; }
  .app-checkbox__icon  { font-size: 8px; }
  .app-checkbox__label { font-size: 12px; }
}

.app-checkbox-group--has-error {
  .app-checkbox__box { border-color: $acg-red; }
}
</style>
